"use client";

import { useCallback } from "react";
import log from "logbeacon";
import { trackEvent } from "@/src/utils/tracking";
import {
  useResponsiveChatOpen,
  type OpenResponsiveChatParams,
} from "@/src/hooks/useResponsiveChatOpen";
import {
  buildGoalCreatorQuestion,
  buildGoalCreatorWithCategoryQuestion,
  buildGoalReviewQuestion,
  buildPlanCheckupChangedQuestion,
  buildPlanNoDraftGoalQuestion,
} from "./buildPlanGoalChatQuestion";
import { persistPlanDraftGoalIdToStorage } from "./planGoalNavigation";
import { resolvePlanResumeDraftMessage } from "./resolvePlanResumeDraftMessage";
import { removeSaveGoalDraftIdFromStorage } from "@hooks/onboarding/saveGoalDraftIdStorage";

/** 内部统一 Chat 打开参数；PC/移动端由 `useResponsiveChatOpen` 分派 */
export type OpenPlanGoalChatParams = OpenResponsiveChatParams;

export type OpenPlanResumeDraftGoalChatParams = {
  /** 草稿 Goal 一句话总结 — 拼入 plan_guide_resume 首条 question */
  summary: string;
  /** 草稿展示标题 — 用于 resume / activation 首条 question */
  draftTitle?: string;
  /** 后端注入的草稿 goal_id — 写入 LOCAL_STORAGE_KEYS.GOAL_ID */
  draftGoalId: string;
  /** Explicit diagnosis_content.source; Onboarding passes onboarding_v3_0_1. */
  source?: string;
  /** Raw Goal.diagnosis_content when source is resolved from API entity. */
  diagnosisContent?: string;
};

export type OpenGoalReviewChatParams = {
  goalId: string;
  goalTitle: string;
};

export type OpenPlanCheckupChangedChatParams = {
  goalId: string;
  title: string;
  summary: string;
};

/**
 * Plan Goal 相关 Chat 入口（6 种场景）。
 *
 * 打开 Chat 时分端：见 `useResponsiveChatOpen`（大屏跳转 /chat，小屏弹窗）
 *
 * 各场景：
 * 1. `openPlanNoDraftGoalChat` — 无草稿，开 Chat 下发 plan_guide 首条
 * 2. `openPlanResumeDraftGoalChat` — 有草稿，写 GOAL_ID 后开 Chat
 * 3–4. Goal Creator（有/无方向）
 * 5. `openGoalReviewChat` — Goal 详情 Chat with Ed
 * 6. `openPlanCheckupChangedChat` — Checkup 已完成卡 Tell Ed what changed
 */
export function usePlanGoalChatOpen() {
  const { openResponsiveChat } = useResponsiveChatOpen();

  const openPlanGoalChat = useCallback(
    (params: OpenPlanGoalChatParams) => {
      openResponsiveChat(params);
    },
    [openResponsiveChat],
  );

  /** 1. Plan 无草稿 — plan_guide 首条 question，分端开 Chat */
  const openPlanNoDraftGoalChat = useCallback(
    (dimensionCode: string) => {
      const code = dimensionCode.trim();
      removeSaveGoalDraftIdFromStorage();
      if (!code) {
        log.warn("[PlanGoalChat] plan_guide missing dimensionCode");
        return;
      }

      const question = buildPlanNoDraftGoalQuestion(code);
      log.info("[PlanGoalChat] open plan no-draft goal chat", {
        dimensionCode: code,
        question,
      });
      openPlanGoalChat({
        question,
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 2. Plan 有草稿 — LOCAL_STORAGE_KEYS.GOAL_ID + 弹窗续建 */
  const openPlanResumeDraftGoalChat = useCallback(
    ({
      summary,
      draftTitle,
      draftGoalId,
      source,
      diagnosisContent,
    }: OpenPlanResumeDraftGoalChatParams) => {
      persistPlanDraftGoalIdToStorage(draftGoalId);
      const question = resolvePlanResumeDraftMessage({
        draftGoalId,
        draftTitle: draftTitle ?? "",
        summary,
        source,
        diagnosisContent,
      });
      log.info("[PlanGoalChat] resume draft goal chat", {
        draftGoalId,
        draftTitle,
        summary,
        source,
      });
      openPlanGoalChat({
        question,
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 3. 有方向 */
  const openGoalCreatorWithCategoryChat = useCallback(
    (categoryName: string) => {
      removeSaveGoalDraftIdFromStorage();
      openPlanGoalChat({
        question: buildGoalCreatorWithCategoryQuestion(categoryName),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 4. 无方向 */
  const openGoalCreatorChat = useCallback(() => {
    removeSaveGoalDraftIdFromStorage();
    openPlanGoalChat({
      question: buildGoalCreatorQuestion(),
      autoRenew: true,
    });
  }, [openPlanGoalChat]);

  /** 5. Goal 详情 — Chat with Ed（review） */
  const openGoalReviewChat = useCallback(
    ({ goalId, goalTitle }: OpenGoalReviewChatParams) => {
      log.info("[PlanGoalChat] open goal review chat", {
        goalId,
        goalTitle,
      });
      trackEvent("chat_data_collection_started", {
        goal_id: goalId,
        entry_from: "goal_detail",
      });
      openPlanGoalChat({
        question: buildGoalReviewQuestion({ goalId, goalTitle }),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 6. Checkup 已完成卡 — Tell Ed what changed（Ed 首条，用户不 autoSubmit） */
  const openPlanCheckupChangedChat = useCallback(
    ({ goalId, title, summary }: OpenPlanCheckupChangedChatParams) => {
      log.info("[PlanGoalChat] open plan checkup changed chat", {
        goalId,
        title,
        summary,
      });
      removeSaveGoalDraftIdFromStorage();
      openPlanGoalChat({
        question: buildPlanCheckupChangedQuestion({ goalId, title, summary }),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  return {
    openPlanGoalChat,
    openPlanNoDraftGoalChat,
    openPlanResumeDraftGoalChat,
    openGoalCreatorWithCategoryChat,
    openGoalCreatorChat,
    openGoalReviewChat,
    openPlanCheckupChangedChat,
  };
}
